import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Image, Row, Spinner } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import Dropzone, { useDropzone } from "react-dropzone";
import { FaFileUpload, FaLink, FaRegTrashAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import API from "../Utility";
import "./PubHubAsset.scss";

export default function PubHubAsset({
  asset,
  onChanged,
  assetType,
  maxFileSizeInBytes,
  acceptMimeTypes,
}) {
  const [isAssetSearchBusy, setIsAssetSearchBusy] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState();
  const [assetSearchResults, setAssetSearchResults] = useState([]);
  const [selectedAssetSearchResult, setSelectedAssetSearchResult] = useState();
  const [currentAsset, setCurrentAsset] = useState(asset);
  const [submitError, setSubmitError] = useState();
  const [result, setResult] = useState();
  const assetSearchTypeaheadRef = useRef();
  // API Config
  const { project } = useParams(); // project needed for API
  let formData = new FormData();
  const apiCallConfig = {
    // url: `/admin/${project}/assets/upload`,
    url: `/admin/${project}/assetpackages/file?contentSourceId=${project}`,
  };

  useEffect(() => {
    if (asset && !currentAsset) {
      setCurrentAsset(asset);
    }
  }, [asset, currentAsset]);

  function determineMimeTypes() {
    if (acceptMimeTypes) {
      return acceptMimeTypes;
    }
    if (assetType) {
      switch (assetType) {
        case "css":
          return ["text/css"];
        case "html":
          return ["text/html"];
        case "video":
          return ["video/mp4"];
        case "image":
          return ["image/jpeg", "image/png", "image/gif", "image/svg+xml"];
      }
    }
    return "";
  }

  useEffect(() => {
    if (selectedAssetSearchResult) {
      setCurrentAsset(selectedAssetSearchResult[0]);
      onChanged(selectedAssetSearchResult[0]);
    }
  }, [selectedAssetSearchResult]);

  function validate(values) {
    const errors = {};
    if (values.file === null || values.file === undefined) {
      errors.file = "Required";
    }
    return errors;
  }
  const submitForm = (values, { setSubmitting }) => {
    const callApi = async function() {
      try {
        setSubmitting(true);
        formData.append("newAssetpackageFile", values.file);
        formData.append("packageName", project);
        apiCallConfig.data = formData;
        const res = await API.postFile(apiCallConfig);
        setSubmitting(false);
        setResult(res.asset);
        setSubmitError();
        if (selectedAssetSearchResult) {
          setSelectedAssetSearchResult();
        }
        setCurrentAsset(res.asset);
        onChanged(res.asset);

        // onSuccess(res);
      } catch (err) {
        setSubmitting(false);
        if (!err.response) {
          setSubmitError(err && err.message);
        } else {
          setSubmitError(
            (err &&
              err.response &&
              err.response.data &&
              err.response.data.message) ||
              (err && err.response && err.response.statusText)
          );
        }
      }
    };
    callApi();
  };
  const onDrop = useCallback((acceptedFiles, setFieldError, setFieldValue) => {
    if (!acceptedFiles || acceptedFiles.length !== 1) {
      alert("Please drop 1 file only");
      return;
    }
    if (acceptMimeTypes) {
      const matchFound = checkFileType(acceptMimeTypes, acceptedFiles[0]);
      if (!matchFound) {
        setFieldError(
          "file",
          `File type is not allowed (must be one of the following: ${acceptMimeTypes
            .join(", ")
            .trim(",")})`
        );
        return;
      }
    }
    function checkFileType(mimeTypes, file) {
      let matchFound = false;
      mimeTypes.forEach((acceptMimeType) => {
        if (!matchFound && acceptMimeType === file.type) {
          matchFound = true;
        }
      });
    }
    if (assetType) {
      let matchFound = false;
      switch (assetType) {
        case "css":
          matchFound = checkFileType(["text/css"], acceptedFiles[0]);
          break;
        case "html":
          matchFound = checkFileType(["text/html"], acceptedFiles[0]);
          break;
        case "video":
          matchFound = checkFileType(["video/mp4"], acceptedFiles[0]);
          break;
        case "image":
          matchFound = checkFileType(["image/*"], acceptedFiles[0]);
          break;
        // no default, just ignore
      }
    }
    if (maxFileSizeInBytes && acceptedFiles[0].size > maxFileSizeInBytes) {
      setFieldError(
        "file",
        `File is larger than the maximum allowed (${
          Math.round((maxFileSizeInBytes / 1024 / 1024) * 100) / 100
        }MB)`
      );
      return;
    }
    setFieldValue("file", acceptedFiles[0], false);
    setFieldError("file");
    // setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const onCancel = (setFieldValue, setFieldError) => {
    setFieldValue("file", null, false);
    setFieldError("file");
  };

  const removeAsset = (e, doIt) => {
    if (doIt) {
      if (selectedAssetSearchResult) {
        setSelectedAssetSearchResult();
      }
      setCurrentAsset(null);
      onChanged(null);
      setShowConfirmRemove(false);
    } else {
      setShowConfirmRemove(false);
    }
  };

  const selectAssetSearchResult = async(selected) => {
    console.log(selected);
    // alert(JSON.stringify(selected));
  };
  const doAssetSearch = async(query) => {
    if (query.length < 3) {
      return;
    }
    try {
      setIsAssetSearchBusy(true);
      // api call
      const assetSearchUrl = `/admin/${project}/assetpackages`;
      const data = await API.get({
        url: assetSearchUrl,
        params: {
          name: project,
          search: query,
        },
      });
      setAssetSearchResults(
        data.map((d) => {
          return {
            ...{ id: d.path },
            ...d,
          };
        })
      );
    } catch (error) {
      setAssetSearchResults();
      console.error(error);
    } finally {
      setIsAssetSearchBusy(false);
    }
  };

  function renderAssetPreview() {
    switch (assetType) {
      case "image":
        return (
          <Image
            src={currentAsset.path}
            style={{
              maxWidth: "100%",
            }}
          />
        );
      case "video":
        return (
          <video
            controls
            src={currentAsset.path}
            style={{
              maxWidth: "100%",
            }}
          />
        );
      default:
        return <div>No preview...</div>;
    }
  }

  if (currentAsset) {
    return (
      <Row className="align-items-center pubhub-asset-form py-2">
        <ConfirmationModal
          title={`Please confirm`}
          body={`Unlink ${currentAsset.name}?`}
          show={showConfirmRemove}
          confirmtext="OK"
          onHide={removeAsset}
        />
        <Col md={3} className="py-1">
          {assetType ? renderAssetPreview() : <div>No preview...</div>}
        </Col>
        <Col className="">
          <div className="">Name: {currentAsset.name}</div>
          <div>
            Size:{" "}
            {currentAsset.size > 1024 * 1024
              ? `${Math.round((currentAsset.size / 1024 / 1024) * 100) / 100}MB`
              : `${Math.round((currentAsset.size / 1024) * 100) / 100}KB`}
          </div>
        </Col>
        <Col md="auto">
          <Button
            href={currentAsset.path}
            target="_blank"
            rel="noopener noreferrer"
            variant="link"
            className="pl-2"
          >
            <FaLink style={{ verticalAlign: "middle" }} />
          </Button>
          <Button
            onClick={() => {
              setShowConfirmRemove(true);
            }}
            variant="link"
            className="pl-2"
            style={{ textDecoration: "none" }}
          >
            <FaRegTrashAlt style={{ verticalAlign: "middle" }} />
          </Button>
        </Col>
        {(currentAsset && !asset) ||
        (asset && !currentAsset) ||
        (asset && currentAsset && asset.path !== currentAsset.path) ? (
            <Col md={12}>
              <Alert variant="info">Unsaved changes</Alert>
            </Col>
          ) : (
            <></>
          )}
      </Row>
    );
  } else {
    return (
      <Formik validate={validate} onSubmit={submitForm} initialValues={{}}>
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          isValid,
          errors,
          submitCount,
          setFieldValue,
          setFieldError,
          isSubmitting,
        }) => (
          <Row className="align-items-center pubhub-asset-form">
            <Col md={12} className="my-1">
              <AsyncTypeahead
                id={'ph-assets-typeahead'}
                ref={assetSearchTypeaheadRef}
                promptText="Search existing assets by name"
                searchText="Searching..."
                delay={300}
                isLoading={isAssetSearchBusy}
                labelKey={(option) => `${option.name} (${option.path})`}
                onSearch={doAssetSearch}
                options={assetSearchResults}
                onChange={setSelectedAssetSearchResult}
                selected={selectedAssetSearchResult}
                placeholder="Search existing assets by name"
                className="typeahead"
              />
            </Col>
            <Col md={12} className="my-1">
              <Row className="align-items-center">
                <Col className="my-3">
                  <Dropzone
                    onDrop={(e) => {
                      onDrop(e, setFieldError, setFieldValue);
                    }}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <div
                        {...getRootProps()}
                        className="upload_users_modal--dropzone"
                      >
                        <input
                          {...getInputProps()}
                          accept={determineMimeTypes()}
                        />
                        <span>
                          {values.file
                            ? `${values.file.name}`
                            : "Click here to select a new file. You may also drag-and-drop a file here."}
                        </span>
                        {errors.file ? (
                          <Form.Control.Feedback
                            type="invalid"
                            style={errors.file ? { display: "block" } : {}}
                          >
                            {errors.file}
                          </Form.Control.Feedback>
                        ) : null}
                      </div>
                    )}
                  </Dropzone>
                </Col>
                {values.file ? (
                  <Col md="auto" className="my-3">
                    {!isSubmitting ? (
                      <>
                        <Button
                          onClick={() => {
                            onCancel(setFieldValue, setFieldError);
                          }}
                          variant="outline-secondary"
                          className="mr-2"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outline-primary"
                          type="button"
                          onClick={handleSubmit}
                        >
                          Upload
                        </Button>
                      </>
                    ) : (
                      <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                      </Spinner>
                    )}
                  </Col>
                ) : (
                  <></>
                )}
              </Row>
            </Col>
            <Col md={12}>
              <div>
                <Row className="align-items-centre">
                  <Col className="my-auto"></Col>
                  {
                    //   <Col className="col-md-auto my-auto">
                    //   <Button
                    //     onClick={removeAsset}
                    //     variant="outline"
                    //     className="px-0"
                    //     style={{ textDecoration: "none" }}
                    //   >
                    //     <FaFileUpload style={{ verticalAlign: "middle" }} />
                    //   </Button>
                    // </Col>
                  }
                </Row>
                <Row>
                  <Col></Col>
                </Row>
                {submitError && (
                  <Alert variant="danger">Error: {submitError}</Alert>
                )}
              </div>
            </Col>
            {(currentAsset && !asset) ||
            (asset && !currentAsset) ||
            (asset && currentAsset && asset.path !== currentAsset.path) ? (
                <Col md={12} className="my-1">
                  <Alert variant="info">Unsaved changes</Alert>
                </Col>
              ) : (
                <></>
              )}
          </Row>
        )}
      </Formik>
    );
  }
}
